
import { useState, useRef, useCallback, useEffect } from "react";
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { RotateCw, ZoomIn, Check, X, Crop as CropIcon } from "lucide-react";
import imageCompression from "browser-image-compression";
import 'react-image-crop/dist/ReactCrop.css';

interface ImageCropperProps {
  imageFile: File;
  onCropComplete: (croppedFile: File) => void;
  onCancel: () => void;
  aspectRatio?: number;
  maxWidth?: number;
  maxHeight?: number;
}

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

const MAX_SIZE = 1200;
const DEFAULT_ASPECT_RATIO = 1;
const FILE_SIZE_LIMIT = 1024 * 1024; // 1MB

export function ImageCropper({
  imageFile,
  onCropComplete,
  onCancel,
  aspectRatio = DEFAULT_ASPECT_RATIO,
  maxWidth = MAX_SIZE,
  maxHeight = MAX_SIZE
}: ImageCropperProps) {
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [zoom, setZoom] = useState<number[]>([1]);
  const [rotation, setRotation] = useState<number>(0);
  const [imgSrc, setImgSrc] = useState<string>('');
  const [activeAspect, setActiveAspect] = useState<string>(
    aspectRatio === 1 ? '1:1' : 
    aspectRatio === 16/9 ? '16:9' : 
    aspectRatio === 4/3 ? '4:3' : 'free'
  );
  const imgRef = useRef<HTMLImageElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  
  // Set initial aspect ratio based on props
  useEffect(() => {
    if (aspectRatio === 1) setActiveAspect('1:1');
    else if (aspectRatio === 16/9) setActiveAspect('16:9');
    else if (aspectRatio === 4/3) setActiveAspect('4:3');
    else setActiveAspect('free');
  }, [aspectRatio]);

  // Load the image when file changes
  useEffect(() => {
    if (!imageFile) return;
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setImgSrc(reader.result?.toString() || '');
    });
    reader.readAsDataURL(imageFile);
  }, [imageFile]);

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    
    // Set initial crop based on provided aspect ratio
    const currentAspect = activeAspect === 'free' 
      ? width / height 
      : activeAspect === '16:9' 
        ? 16/9 
        : activeAspect === '4:3' 
          ? 4/3 
          : 1;
          
    setCrop(centerAspectCrop(width, height, currentAspect));
  }, [activeAspect]);

  const handleAspectChange = (newRatio: string) => {
    setActiveAspect(newRatio);
    
    if (!imgRef.current) return;
    const { width, height } = imgRef.current;
    
    let newAspectRatio: number;
    switch (newRatio) {
      case '4:3':
        newAspectRatio = 4 / 3;
        break;
      case '16:9':
        newAspectRatio = 16 / 9;
        break;
      case 'free':
        // Allow free-form cropping
        setCrop(undefined);
        return;
      default: // 1:1
        newAspectRatio = 1;
    }
    
    setCrop(centerAspectCrop(width, height, newAspectRatio));
  };

  const applyRotation = (delta: number) => {
    setRotation((prev) => {
      let newRotation = prev + delta;
      // Normalize rotation to 0-360
      while (newRotation >= 360) newRotation -= 360;
      while (newRotation < 0) newRotation += 360;
      return newRotation;
    });
  };

  // Create a cropped image
  const getCroppedImage = async () => {
    if (!imgRef.current || !completedCrop) return null;
    
    setIsProcessing(true);
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        throw new Error('No 2d context');
      }

      const image = imgRef.current;
      
      // Set dimensions
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      
      // Calculate dimensions based on crop
      const cropX = completedCrop.x * scaleX;
      const cropY = completedCrop.y * scaleY;
      let cropWidth = completedCrop.width * scaleX;
      let cropHeight = completedCrop.height * scaleY;
      
      // Calculate target dimensions to maintain the aspect ratio
      let targetWidth = cropWidth;
      let targetHeight = cropHeight;
      
      // Apply zoom to the dimensions (smaller zoom value = smaller visual size)
      targetWidth = targetWidth * zoom[0];
      targetHeight = targetHeight * zoom[0];
      
      // Handle rotation - swap dimensions if needed
      if (rotation % 180 !== 0) {
        // For 90 or 270 degree rotations, swap width and height
        [targetWidth, targetHeight] = [targetHeight, targetWidth];
      }
      
      // Limit width and height while maintaining aspect ratio
      if (targetWidth > maxWidth) {
        const ratio = maxWidth / targetWidth;
        targetWidth = maxWidth;
        targetHeight = targetHeight * ratio;
      }
      
      if (targetHeight > maxHeight) {
        const ratio = maxHeight / targetHeight;
        targetHeight = maxHeight;
        targetWidth = targetWidth * ratio;
      }
      
      // Set canvas size (must match the final image size)
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Handle rotation
      if (rotation > 0) {
        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        
        // If rotated 90 or 270 degrees, we need to adjust the drawing
        if (rotation % 180 !== 0) {
          ctx.translate(-canvas.height / 2, -canvas.width / 2);
        } else {
          ctx.translate(-canvas.width / 2, -canvas.height / 2);
        }
      }
      
      // Draw the cropped image
      if (rotation % 180 === 0) {
        // No rotation or 180 degree rotation
        ctx.drawImage(
          image,
          cropX,
          cropY,
          cropWidth,
          cropHeight,
          rotation > 0 ? 0 : 0,
          rotation > 0 ? 0 : 0,
          targetWidth,
          targetHeight
        );
      } else {
        // 90 or 270 degree rotation
        ctx.drawImage(
          image,
          cropX,
          cropY,
          cropWidth,
          cropHeight,
          0,
          0,
          targetHeight, // Swap width and height
          targetWidth
        );
      }
      
      if (rotation > 0) {
        ctx.restore();
      }
      
      // Convert to blob
      return new Promise<File>((resolve) => {
        canvas.toBlob(async (blob) => {
          if (!blob) {
            throw new Error('Canvas is empty');
          }
          
          // Compress the image if it's large
          let finalBlob = blob;
          
          if (blob.size > FILE_SIZE_LIMIT) {
            const options = {
              maxSizeMB: 1,
              maxWidthOrHeight: Math.max(targetWidth, targetHeight),
              useWebWorker: true,
            };
            
            try {
              finalBlob = await imageCompression(new File([blob], 'image.jpg'), options);
            } catch (error) {
              console.error('Compression error:', error);
            }
          }
          
          // Create a file from the blob
          const cropFile = new File(
            [finalBlob], 
            `cropped-${imageFile.name}`,
            { 
              type: imageFile.type,
              lastModified: Date.now() 
            }
          );
          
          resolve(cropFile);
        }, imageFile.type);
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCropComplete = async () => {
    if (!completedCrop) return;
    try {
      const croppedFile = await getCroppedImage();
      if (croppedFile) {
        onCropComplete(croppedFile);
      }
    } catch (error) {
      console.error('Error cropping image:', error);
    }
  };

  // Generate aspect ratio button classes
  const getButtonClasses = (buttonAspect: string) => {
    return buttonAspect === activeAspect ? 'default' : 'outline';
  };

  return (
    <div className="flex flex-col space-y-4">
      <div className="flex flex-wrap gap-2 justify-center mb-2">
        <Button 
          size="sm" 
          variant={getButtonClasses('1:1')} 
          onClick={() => handleAspectChange('1:1')}
        >
          Square (1:1)
        </Button>
        <Button 
          size="sm" 
          variant={getButtonClasses('4:3')} 
          onClick={() => handleAspectChange('4:3')}
        >
          4:3
        </Button>
        <Button 
          size="sm" 
          variant={getButtonClasses('16:9')} 
          onClick={() => handleAspectChange('16:9')}
        >
          16:9
        </Button>
        <Button 
          size="sm" 
          variant={getButtonClasses('free')} 
          onClick={() => handleAspectChange('free')}
        >
          Free
        </Button>
      </div>
      
      <div className="relative flex justify-center border border-input rounded-md p-1 overflow-hidden">
        {imgSrc ? (
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={activeAspect === 'free' ? undefined : activeAspect === '4:3' ? 4/3 : activeAspect === '16:9' ? 16/9 : 1}
            circularCrop={false}
          >
            <img
              ref={imgRef}
              alt="Crop preview"
              src={imgSrc}
              style={{ 
                transform: `scale(${zoom[0]}) rotate(${rotation}deg)`,
                maxHeight: '500px',
                maxWidth: '100%',
                objectFit: 'contain'
              }}
              onLoad={onImageLoad}
            />
          </ReactCrop>
        ) : (
          <div className="flex items-center justify-center h-64 w-full">
            <CropIcon className="h-12 w-12 text-muted-foreground" />
          </div>
        )}
      </div>
      
      <div className="flex items-center space-x-2">
        <ZoomIn className="h-5 w-5 text-muted-foreground" />
        <Slider
          value={zoom}
          min={0.1}
          max={3}
          step={0.05}
          onValueChange={setZoom}
          className="flex-1"
        />
      </div>
      
      <div className="flex justify-between gap-2">
        <Button 
          variant="outline" 
          onClick={() => applyRotation(-90)}
        >
          <RotateCw className="h-4 w-4 mr-2 -scale-x-100" /> 
          Rotate Left
        </Button>
        <Button 
          variant="outline" 
          onClick={() => applyRotation(90)}
        >
          <RotateCw className="h-4 w-4 mr-2" /> 
          Rotate Right
        </Button>
      </div>
      
      <div className="flex justify-end gap-2 mt-4">
        <Button 
          variant="outline" 
          onClick={onCancel}
        >
          <X className="h-4 w-4 mr-2" /> 
          Cancel
        </Button>
        <Button 
          onClick={handleCropComplete}
          disabled={!completedCrop || isProcessing}
        >
          {isProcessing ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </span>
          ) : (
            <>
              <Check className="h-4 w-4 mr-2" /> 
              Apply Crop
            </>
          )}
        </Button>
      </div>
      
      <canvas
        ref={previewCanvasRef}
        style={{
          display: 'none',
        }}
      />
    </div>
  );
}
